import { DEFAULT_LNGS_NAMESPACE } from '@ingenico/util-i18n-config';
import { AppRootLayout, UserSettings } from '@ingenico/react-mfe-shell-utils';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';

import customerNavigation from '../../../mfe/navigation';
import tenantNavigation from '../../../mfe/navigation-tenant';
import developerNavigation from '../../../mfe/navigation-developer';
import privateNavigation from '../../../mfe/navigation-private';
import { DeviceManagementSpiAdapter } from '../../hexa/adapters/deviceManagementSpi.adapter';
import { appContainer } from '../../hexa/appContainer.const';
import { UserTypeSwitchService } from '../../services/userTypeSwitch.service';
import { useRootVM } from './useRoot.vm';
import type { NavigationConfig } from '@ingenico/react-section-utils';
import type { DeviceManagementSpiPort } from '../../../domain/domain/hexa/ports/spi.model';

function customerNavigationStandaloneApp(spi: DeviceManagementSpiPort): NavigationConfig {
  const defaultCustomerNavigation = customerNavigation(spi);
  const privateNavigationStandaloneApp = privateNavigation(spi);
  return [...privateNavigationStandaloneApp, ...defaultCustomerNavigation];
}

export function RootPage(): ReactElement {
  const { t } = useTranslation(DEFAULT_LNGS_NAMESPACE.COMMON);
  const vm = useRootVM();

  const navigation = useMemo(() => {
    if (UserTypeSwitchService.isDeveloperUser()) {
      return developerNavigation;
    } else if (UserTypeSwitchService.isTenantUser()) {
      return tenantNavigation;
    } else {
      return customerNavigationStandaloneApp;
    }
  }, []);

  return (
    <AppRootLayout
      currentPageEntityLabelSubject={vm.currentPageEntityLabelSubject}
      headerBarCustomBackgroundColorSubject={vm.headerBarCustomBackgroundColorSubject}
      headerMobileInformationSubject={vm.headerMobileInformationSubject}
      navigation={navigation(appContainer.get(DeviceManagementSpiAdapter))}
      navigationPrefix={vm.organizationName}
      rightHeaderComponents={[
        <UserSettings
          options={[
            {
              key: 'my-account',
              onClick: vm.handleMyProfileFn,
              icon: 'account_circle',
              value: t('header.myAccount'),
            },
            {
              key: 'logout',
              onClick: vm.logoutFn,
              icon: 'lock',
              value: t('header.logout'),
            },
          ]}
          user={vm.currentUser ?? {}}
        />,
      ]}
    >
      <Outlet />
    </AppRootLayout>
  );
}
